/**
 * First landing on this browser. UTM params and the referrer are kept as
 * they were the first time. A later visit does not replace them. An invite
 * token can be filled in afterwards, because it is a separate link.
 */

export const FIRST_TOUCH_KEY = 'brainstormy.firstTouch.v1';
export const FIRST_TOUCH_POSTED_KEY = 'brainstormy.firstTouch.posted';

function clip(value, limit) {
  if (value == null) return null;
  const text = String(value).replace(/\0/g, '').trim();
  if (!text) return null;
  return text.slice(0, limit);
}

export function externalReferrer(referrer, origin) {
  const text = clip(referrer, 500);
  if (!text) return null;
  try {
    const url = new URL(text);
    if (origin && url.origin === origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return text;
  } catch {
    return null;
  }
}

export function readFirstTouch(storage = window.localStorage) {
  try {
    const raw = storage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      utm_source: clip(parsed.utm_source, 120),
      utm_medium: clip(parsed.utm_medium, 120),
      utm_campaign: clip(parsed.utm_campaign, 120),
      referrer: clip(parsed.referrer, 500),
      invite: clip(parsed.invite, 200),
      captured_at: parsed.captured_at || null,
    };
  } catch {
    return null;
  }
}

function writeTouch(storage, touch) {
  storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
}

export function captureFirstTouch({
  storage = window.localStorage,
  search = '',
  referrer = '',
  origin = '',
  now = () => new Date().toISOString(),
} = {}) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const incomingInvite = clip(params.get('invite'), 200);
  let existing = readFirstTouch(storage);
  if (existing) {
    if (!existing.invite && incomingInvite) {
      existing = { ...existing, invite: incomingInvite };
      try {
        writeTouch(storage, existing);
      } catch {
        return existing;
      }
    }
    return existing;
  }

  const touch = {
    utm_source: clip(params.get('utm_source'), 120),
    utm_medium: clip(params.get('utm_medium'), 120),
    utm_campaign: clip(params.get('utm_campaign'), 120),
    referrer: externalReferrer(referrer, origin),
    invite: incomingInvite,
    captured_at: now(),
  };
  try {
    writeTouch(storage, touch);
  } catch {
    return touch;
  }
  return touch;
}

export function touchMarker(touch) {
  if (!touch) return '';
  return [
    touch.utm_source,
    touch.utm_medium,
    touch.utm_campaign,
    touch.referrer,
    touch.invite,
  ].join('\n');
}

export function wasPosted(touch, storage = window.localStorage) {
  try {
    return storage.getItem(FIRST_TOUCH_POSTED_KEY) === touchMarker(touch);
  } catch {
    return false;
  }
}

export function markPosted(touch, storage = window.localStorage) {
  try {
    storage.setItem(FIRST_TOUCH_POSTED_KEY, touchMarker(touch));
  } catch {
    // The next signed-in load will try again.
  }
}
